import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  alpha,
  styled
} from '@mui/material';
import { ReactNode, useState } from 'react';

// Styled components for modern table appearance
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
  '& .MuiTable-root': {
    borderCollapse: 'separate',
    borderSpacing: 0,
  }
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.04),
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    padding: theme.spacing(2),
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    '&:first-of-type': {
      paddingLeft: theme.spacing(3),
    },
    '&:last-of-type': {
      paddingRight: theme.spacing(3),
    },
  }
}));

const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '& .MuiTableRow-root': {
    '&:hover': {
      backgroundColor: alpha(theme.palette.action.hover, 0.04),
    },
    '&:last-child .MuiTableCell-root': {
      borderBottom: 'none',
    }
  },
  '& .MuiTableCell-root': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    padding: theme.spacing(2),
    fontSize: '0.875rem',
    '&:first-of-type': {
      paddingLeft: theme.spacing(3),
    },
    '&:last-of-type': {
      paddingRight: theme.spacing(3),
    },
  }
}));

export interface TableColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  width?: string | number;
  render?: (value: any, row: any) => ReactNode;
}

export interface TableRow {
  id: string | number;
  [key: string]: any;
}

interface ModernTableProps {
  columns: TableColumn[];
  rows: TableRow[];
  title?: string;
  emptyMessage?: string;
  maxHeight?: number | string;
  maxRowsPerPage?: number;
  showPagination?: boolean;
  rowsPerPageOptions?: number[];
}

export const ModernTable = ({
  columns,
  rows,
  title,
  emptyMessage = 'No data available',
  maxHeight,
  maxRowsPerPage = 10,
  showPagination = true,
  rowsPerPageOptions = [5, 10, 25, 50]
}: ModernTableProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(maxRowsPerPage);

  // Calculate paginated rows
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRows = showPagination ? rows.slice(startIndex, endIndex) : rows;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
      {title && (
        <Typography variant="h6" sx={{ p: 3, pb: 0 }}>
          {title}
        </Typography>
      )}
      <StyledTableContainer
        sx={{
          maxHeight: maxHeight || 'auto',
        }}
      >
        <Table stickyHeader={!!maxHeight}>
          <StyledTableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || 'left'}
                  sx={{
                    width: column.width,
                    minWidth: column.width
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </StyledTableHead>
          <StyledTableBody>
            {paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{
                    py: 4,
                    color: 'text.secondary',
                    fontStyle: 'italic'
                  }}
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row) => (
                <TableRow key={row.id}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align || 'left'}
                    >
                      {column.render
                        ? column.render(row[column.key], row)
                        : row[column.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </StyledTableBody>
        </Table>
      </StyledTableContainer>

      {showPagination && rows.length > 0 && (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
          sx={(theme) => ({
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            '& .MuiTablePagination-toolbar': {
              paddingLeft: theme.spacing(3),
              paddingRight: theme.spacing(3),
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.875rem',
            },
          })}
        />
      )}
    </Paper>
  );
};

export default ModernTable; 